import { useEffect, useRef, useState } from "react";
import {
  DRAFT_VALIDATION_PAGE_SIZE,
  useDraftValidationListQuery,
  useDraftValidationDetailQuery,
  useDraftValidationSummaryQuery,
  useUpdateAdminDraftMutation,
} from "../../api/queries/adminQueries";
import type { AdminOrganization } from "../../utils/types";
import { toast } from "sonner";
import { DraftValidationOrgSummary } from "./DraftValidationOrgSummary";
import { OrgSelector } from "./OrgSelector";
import { DraftListTable } from "./DraftListTable";
import { DraftDetailPanel } from "./DraftDetailPanel";
import type { DraftBodyEditorRef } from "./DraftEditorSection";
import { formatAppDateTime } from "../../utils/date";

type DraftValidationDraftsTabProps = {
  organizations: AdminOrganization[];
};

export const DraftValidationDraftsTab = ({
  organizations,
}: DraftValidationDraftsTabProps) => {
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [selectedDraftUid, setSelectedDraftUid] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [editSubject, setEditSubject] = useState("");
  const [editTo, setEditTo] = useState<string[]>([]);
  const bodyEditorRef = useRef<DraftBodyEditorRef>(null);

  const {
    data: draftsPage,
    isLoading: isDraftsLoading,
    isFetching: isDraftsFetching,
  } = useDraftValidationListQuery(selectedOrgId, page);
  const { data: summary, isLoading: isSummaryLoading } =
    useDraftValidationSummaryQuery(selectedOrgId);
  const { data: draftDetail, isLoading: isDetailLoading } =
    useDraftValidationDetailQuery(selectedDraftUid, selectedOrgId);
  const updateDraft = useUpdateAdminDraftMutation(
    selectedDraftUid,
    selectedOrgId,
  );

  const draftsList = draftsPage?.items ?? [];
  const totalDrafts = draftsPage?.total ?? 0;
  const totalPages = Math.max(
    1,
    Math.ceil(totalDrafts / DRAFT_VALIDATION_PAGE_SIZE),
  );

  useEffect(() => {
    if (draftDetail) {
      setEditSubject(draftDetail.subject ?? "");
      setEditTo(draftDetail.to ?? []);
    }
  }, [draftDetail]);

  const formatDate = (dateString: string) => formatAppDateTime(dateString);

  const handleSaveDraft = () => {
    if (!draftDetail || !selectedDraftUid || !selectedOrgId) return;
    const html = bodyEditorRef.current?.getHTML() ?? "";
    updateDraft.mutate(
      {
        text: html,
        subject: editSubject,
        to: editTo,
        cc: draftDetail.cc ?? null,
        bcc: draftDetail.bcc ?? null,
        thread_id: draftDetail.thread_id ?? null,
        reply_message_id: draftDetail.reply_message_id ?? null,
      },
      { onSuccess: () => toast.success("Draft saved successfully") },
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <OrgSelector
        organizations={organizations}
        selectedOrgId={selectedOrgId}
        onChange={(orgId) => {
          setSelectedOrgId(orgId);
          setSelectedDraftUid(null);
          setPage(1);
        }}
      />

      {selectedOrgId ? (
        <>
          <DraftValidationOrgSummary
            summary={summary}
            isLoading={isSummaryLoading}
          />
          {selectedDraftUid ? (
            <div className="flex w-full flex-col gap-4">
              <button
                type="button"
                onClick={() => setSelectedDraftUid(null)}
                className="w-fit text-sm font-medium text-[#4B7F73] hover:underline"
              >
                ← Back to drafts
              </button>
              <DraftDetailPanel
                selectedDraftUid={selectedDraftUid}
                draftDetail={draftDetail}
                isLoading={isDetailLoading}
                editSubject={editSubject}
                editTo={editTo}
                onSubjectChange={setEditSubject}
                onToChange={setEditTo}
                onSave={handleSaveDraft}
                isSaving={updateDraft.isPending}
                bodyEditorRef={bodyEditorRef}
                formatDate={formatDate}
              />
            </div>
          ) : (
            <DraftListTable
              drafts={draftsList}
              totalDrafts={totalDrafts}
              page={page}
              totalPages={totalPages}
              isLoading={isDraftsLoading}
              isFetching={isDraftsFetching}
              onPageChange={setPage}
              onSelect={setSelectedDraftUid}
              formatDate={formatDate}
            />
          )}
        </>
      ) : (
        <p className="text-sm text-slate-600">
          Select an organization to review drafts.
        </p>
      )}
    </div>
  );
};
